import {
  useDateFormat,
  useProperty,
  useSearch,
  useSiteContext,
  useTranslation,
} from '@eti/providers';
import { css, cx, mediaQueries } from '@eti/styles';
import { constants, date, duration as durationUtil } from '@eti/utils';
import { CarrierLogo, Icon } from '@eti/components';
import { Inline, LinkButton, Stack, Text, textVariants, vars } from '@etg/wings';
import type { Dispatch, SetStateAction } from 'react';
import { TripType } from '@eti/schema-types';
import { ALL, SOME } from '../../../../../../constants/transportationMeansConstants';
import Stops from '../../../../../../widgets/trip-details/components/Stops';
import { Label } from '../../../v1/styles/quickSortButton';
import { StopoverInfoIndicator } from '../../resultTripCards/components/TripIndicator';
import { useBrandedFareName } from '../../../../../../common/custom-hooks/useBrandedFareName';
import { isMatchingLocation } from '../../../../../../widgets/trip-details/utils/originDestinationUtil';
import type { SearchRouteCodeKindType } from '../../model';
import { SearchRouteCodeKind } from '../../model';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import useGenerateTripBoundAriaLabel from '../../resultTripCards/components/tripDetailsBound/hooks/useGenerateTripBoundAriaLabel';
import { getBoundTitle, getBoundType } from '../../../../../../widgets/trip-details/utils/format';
import type { FinalSegmentsType } from './utils';

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const itineraryInfoStyles = css`
  font-size: 0.875rem;
  line-height: 100%;

  &:not(:last-child) {
    margin-block-end: 16px;
  }
`;
const departureArrivalStyles = css`
  font-weight: 700;
  justify-content: space-between;

  > div {
    position: relative;
  }

  > p {
    font-size: inherit;
    font-weight: inherit;
    margin-block-start: 0;
  }
`;

const departureArrivalCitiesStyles = css`
  color: #737373;
  font-weight: 400;

  span.duration {
    color: ${vars.colors.text.primary};
    font-size: inherit;
    max-width: fit-content;
  }
`;

const itineraryTimeStyles = css`
  font-size: 1rem;
`;

const carrierDateStyles = css`
  color: ${vars.colors.text.primary};
  justify-content: space-between;

  img {
    height: 20px;
    width: 20px;

    &:not(:last-child) {
      margin-inline-end: 8px;
    }
  }
`;

const overnightIndicatorStyles = css`
  background: ${vars.colors.signal.information.background};
  border-radius: 3px;
  color: ${vars.colors.signal.information.foreground};
  padding: 0 2px;
`;

const selfTransferStyles = css`
  justify-content: space-between;

  > div {
    background: ${vars.colors.signal.caution.background};
    border-radius: 8px;
    font-weight: 700;
    padding: 4px 8px;

    > span {
      color: ${vars.colors.signal.caution.foreground};
      margin-left: 5px;
      white-space: nowrap;
    }
  }

  p {
    color: ${vars.colors.text.primary};
  }
`;

const badgesWrapper = css`
  margin-top: 4px;
`;

const cabinClassStyles = css`
  color: #737373;
  font-size: 0.875rem;

  @media ${mediaQueries.small.only} {
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 180px;
  }
`;

const dotSeparatorStyles = css`
  background-color: #737373;
  border-radius: 2px;
  height: 3px;
  width: 3px;
`;

const nearbyAirportStyles = css`
  background: ${vars.colors.signal.caution.background};
  color: ${vars.colors.signal.caution.foreground};
  padding: 0 4px;
`;

const selfTransferLinkButtonStyles = css`
  color: ${vars.colors.promotional.foreground};
`;

const lastRowWrapper = (hasSelfTransferLink: boolean) => css`
  display: flex;
  justify-content: ${hasSelfTransferLink ? 'space-between' : 'flex-end'};
  padding-top: 8px;
  width: 100%;
`;

export type TrainSegmentType = 'ALL' | 'SOME' | 'NONE';
export interface ItineraryDetailsLocationTimeProps {
  segment: FinalSegmentsType;
  setIsItineraryDrawerSelfTransferModalOpen?: Dispatch<SetStateAction<boolean>>;
  selfTransferPlaces?: string[];
  tripType?: TripType;
}

const ItineraryDetailsLocationTime = ({
  segment,
  setIsItineraryDrawerSelfTransferModalOpen,
  selfTransferPlaces,
  tripType,
}: ItineraryDetailsLocationTimeProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const {
    allMarketingCarriers,
    brandedFareInformation,
    boundNumber,
    cabinClassName,
    isSelfTransfer,
    marketingCarrier,
    stops,
    trainSegment,
    totalDuration,
    totalNumberOfTechnicalStops,
  } = segment;
  const arrivalCityCode = segment.destination?.cityCode ?? '';
  const arrivalCode = segment.destination?.code ?? '';
  const arrivalDate = segment.arrivedAt as string;
  const departureCityCode = segment.origin?.cityCode ?? '';
  const departureCode = segment.origin?.code ?? '';
  const departureDate = segment.departuredAt as string;
  const stopoverType = segment?.stopoverType;

  const durationInDays =
    date.durationInDays(new Date(departureDate), new Date(arrivalDate)) ?? null;
  const { time, day } = useDateFormat();
  const { currentSearchData } = useSearch();
  const { language, timeFormat } = useSiteContext();
  const brandedFareName = useBrandedFareName({ brandedFareInformation, marketingCarrier });
  const shouldShowSelfTransferLinkButtonInBounds =
    isVISelfTansferDealEnabled && selfTransferPlaces && selfTransferPlaces.length > 0;
  const shouldShowOldSelfTransfer = isSelfTransfer && !isVISelfTansferDealEnabled;

  const selfTransferPlacesCommaSeparated = selfTransferPlaces?.join(', ');

  const isDifferentAirport = (searchRouteKind: SearchRouteCodeKindType) => {
    if (window.location.pathname.includes(routes.CHANGE_TRIP)) {
      return false;
    }

    if (boundNumber === undefined) {
      return false;
    }

    return (
      isDifferentAirportNotificationEnabled &&
      !isMatchingLocation(currentSearchData?.routes?.[boundNumber][searchRouteKind] ?? '', {
        code: searchRouteKind === SearchRouteCodeKind.ORIGIN_CODE ? departureCode : arrivalCode,
        cityCode:
          searchRouteKind === SearchRouteCodeKind.ORIGIN_CODE
            ? departureCityCode ?? ''
            : arrivalCityCode ?? '',
      })
    );
  };

  const isTechnicalStopsEnabled = p('IbeClient.TripDetails.TechnicalStops.Enabled');
  const shouldShowTechnicalStops = isTechnicalStopsEnabled && totalNumberOfTechnicalStops > 0;

  const segmentBound = {
    arrivedAt: arrivalDate,
    brandedFareInformation: brandedFareInformation ?? null,
    cabinClassName,
    departuredAt: departureDate,
    destination: segment.destination,
    duration: totalDuration,
    isSelfTransfer,
    marketingCarrier,
    origin: segment.origin,
    stops,
  };

  const isMultiStop = tripType && tripType !== TripType.OneWay && tripType !== TripType.Return;

  const title = isMultiStop
    ? t(getBoundTitle(getBoundType(boundNumber - 1), isMultiStop), boundNumber)
    : '';

  const segmentSRAnnouncement = useGenerateTripBoundAriaLabel(
    segmentBound,
    allMarketingCarriers,
    title,
    trainSegment,
    totalNumberOfTechnicalStops,
  );

  return (
    <div
      aria-label={segmentSRAnnouncement}
      className={itineraryInfoStyles}
      data-testid="itinerary-details-location-time-container"
    >
      <Stack aria-hidden spacing={4}>
        <Inline alignY="center" className={carrierDateStyles}>
          <Inline alignY="center" spacing={8}>
            {date.format(departureDate, day)}
            {Boolean(cabinClassName) && <div className={dotSeparatorStyles} />}
            <span className={cabinClassStyles}>
              {brandedFareName || t(`IbeClient.CabinClass.${cabinClassName}.Text`)}
            </span>
          </Inline>
          <div>
            {allMarketingCarriers.map((carrier) => (
              <CarrierLogo
                data-testid="itinerary-details-carrier-logos"
                id={carrier.code ?? ''}
                imageAlt={carrier.name as string}
                key={carrier.code}
              />
            ))}
          </div>
        </Inline>
        <Inline className={departureArrivalStyles}>
          <Inline spacing={4}>
            <span className={itineraryTimeStyles}>{date.format(departureDate, time)} - </span>
            <span className={itineraryTimeStyles}>{date.format(arrivalDate, time)}</span>
            {durationInDays > 0 && (
              <div
                className={overnightIndicatorStyles}
                data-testid="trip-bound-info-overnight"
              >{`+${durationInDays}`}</div>
            )}
          </Inline>
          {stops > 0 && <Stops stops={stops} />}
          {!stops && (
            <Text variant={textVariants._14_BOLD}>{t('TripDetails.Details.DirectFlight')}</Text>
          )}
        </Inline>

        <Inline
          alignY="center"
          className={cx(departureArrivalStyles, departureArrivalCitiesStyles)}
        >
          <Inline spacing={4}>
            <Text
              className={
                isDifferentAirport(SearchRouteCodeKind.ORIGIN_CODE) ? nearbyAirportStyles : null
              }
              data-testid={`itinerary-drawer-origin-${boundNumber}-airport-code`}
            >
              {departureCode}
            </Text>
            <Text>-</Text>
            <Text
              className={
                isDifferentAirport(SearchRouteCodeKind.DESTINATION_CODE)
                  ? nearbyAirportStyles
                  : null
              }
              data-testid={`itinerary-drawer-destination-${boundNumber}-airport-code`}
            >
              {arrivalCode}
            </Text>
          </Inline>
          <Label
            className="duration"
            data-testid="searchResults-segment-duration"
          >{`${LRE}${durationUtil.getDuration(
            totalDuration,
            language.code,
            timeFormat,
            ['h', 'm'],
            true,
          )}${PDF}`}</Label>
        </Inline>
      </Stack>
      {(shouldShowOldSelfTransfer ||
        stopoverType ||
        trainSegment ||
        shouldShowTechnicalStops ||
        shouldShowSelfTransferLinkButtonInBounds) && (
        <Inline alignY="top" aria-hidden noWrap>
          {(shouldShowOldSelfTransfer || stopoverType) && (
            <Stack className={badgesWrapper} spacing={4}>
              {shouldShowOldSelfTransfer && (
                <Inline
                  alignY="center"
                  className={selfTransferStyles}
                  data-testid="itinerary-details-self-transfer"
                >
                  <Inline alignY="center" noWrap spacing={4}>
                    <Icon color="#803D00" id="self-transfer" size="1rem" />
                    <span>{t('TripDetails.Details.SelfTransfer')}</span>
                  </Inline>
                </Inline>
              )}
              {stopoverType && <StopoverInfoIndicator stopoverType={stopoverType} />}
            </Stack>
          )}
          <div className={lastRowWrapper(shouldShowSelfTransferLinkButtonInBounds)}>
            {shouldShowSelfTransferLinkButtonInBounds && (
              <Inline alignY="center" spacing={4}>
                <Icon
                  color={vars.colors.promotional.foreground}
                  data-testid="self-transfer-icon"
                  id="self-transfer"
                  size="20px"
                />
                <LinkButton
                  aria-label={`${t(
                    'IbeClient.Itinerary.Drawer.SelfTransfer.Text',
                  )}, ${selfTransferPlacesCommaSeparated}`}
                  className={selfTransferLinkButtonStyles}
                  data-testid="self-transfer-linkButton"
                  onClick={
                    setIsItineraryDrawerSelfTransferModalOpen
                      ? () => setIsItineraryDrawerSelfTransferModalOpen(true)
                      : undefined
                  }
                >
                  {t('IbeClient.Itinerary.Drawer.SelfTransfer.Text')}
                </LinkButton>
                <span aria-hidden>{selfTransferPlacesCommaSeparated}</span>
              </Inline>
            )}
            <Stack align="end" spacing={4}>
              {shouldShowTechnicalStops && (
                <Text as="p" data-testid="itinerary-details-technical-stops">
                  {totalNumberOfTechnicalStops === 1
                    ? t(
                        'TripDetails.Details.TechnicalStop.Label.Singular',
                        totalNumberOfTechnicalStops,
                      )
                    : t(
                        'TripDetails.Details.TechnicalStop.Label.Plural',
                        totalNumberOfTechnicalStops,
                      )}
                </Text>
              )}
              {trainSegment === SOME && <p>{t('IbeClient.Segment.PartByTrain.Text')}</p>}
              {trainSegment === ALL && <p>{t('IbeClient.Segment.Train.Text')}</p>}
            </Stack>
          </div>
        </Inline>
      )}
    </div>
  );
};

export default ItineraryDetailsLocationTime;
